import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Calendar, TrendingUp, ArrowLeft, Search, ChevronRight } from 'lucide-react';

interface Earning {
  id: string;
  company: string;
  ticker: string;
  date: string;
  time: string;
  expectedEPS: string;
  sector: string;
  marketCap: string;
}

// TODO: Replace with real data from API
const mockEarnings: Earning[] = [
  {
    id: '1',
    company: 'Apple Inc.',
    ticker: 'AAPL',
    date: 'Dec 12, 2025',
    time: 'After Market Close',
    expectedEPS: '$1.54',
    sector: 'Technology',
    marketCap: '$3.2T'
  },
  {
    id: '2',
    company: 'Microsoft Corporation',
    ticker: 'MSFT',
    date: 'Dec 13, 2025',
    time: 'After Market Close',
    expectedEPS: '$2.78',
    sector: 'Technology',
    marketCap: '$2.8T'
  },
  {
    id: '3',
    company: 'NVIDIA Corporation',
    ticker: 'NVDA',
    date: 'Dec 14, 2025',
    time: 'After Market Close',
    expectedEPS: '$0.92',
    sector: 'Technology',
    marketCap: '$1.5T'
  },
  {
    id: '4',
    company: 'Amazon.com Inc.',
    ticker: 'AMZN',
    date: 'Dec 15, 2025',
    time: 'After Market Close',
    expectedEPS: '$1.12',
    sector: 'Consumer Cyclical',
    marketCap: '$1.9T'
  },
  {
    id: '5',
    company: 'Alphabet Inc.',
    ticker: 'GOOGL',
    date: 'Dec 16, 2025',
    time: 'After Market Close',
    expectedEPS: '$1.89',
    sector: 'Technology',
    marketCap: '$2.1T'
  },
  {
    id: '6',
    company: 'Tesla Inc.',
    ticker: 'TSLA',
    date: 'Dec 17, 2025',
    time: 'After Market Close',
    expectedEPS: '$0.85',
    sector: 'Automotive',
    marketCap: '$950B'
  },
  {
    id: '7',
    company: 'Meta Platforms Inc.',
    ticker: 'META',
    date: 'Dec 18, 2025',
    time: 'After Market Close',
    expectedEPS: '$5.23',
    sector: 'Technology',
    marketCap: '$1.3T'
  },
  {
    id: '8',
    company: 'JPMorgan Chase & Co.',
    ticker: 'JPM',
    date: 'Dec 19, 2025',
    time: 'Before Market Open',
    expectedEPS: '$4.12',
    sector: 'Financial Services',
    marketCap: '$580B'
  }
];

const periods = ['This Week', 'Next Week', 'This Month', 'Next Month'];

export function EarningsCalendarPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('This Week');

  const filteredEarnings = mockEarnings.filter(earning => 
    earning.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
    earning.ticker.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <h2 className="tracking-tight">Earnings Calendar</h2>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Title and Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <h1 className="mb-4 text-5xl md:text-6xl tracking-tight">
            Upcoming Earnings
          </h1>
          <p className="text-gray-600 max-w-2xl">
            Track upcoming earnings announcements for major companies and access detailed qualitative analysis
          </p>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 space-y-4"
        >
          {/* Period Selector */}
          <div className="flex gap-3 overflow-x-auto pb-2">
            {periods.map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-6 py-3 rounded-xl transition-all whitespace-nowrap ${
                  selectedPeriod === period
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-200 hover:border-gray-900'
                }`}
              >
                {period}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by company name or ticker..."
              className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-2xl focus:outline-none focus:border-gray-900 transition-colors"
            />
          </div>
        </motion.div>

        {/* Earnings List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="space-y-3"
        >
          {filteredEarnings.map((earning, index) => (
            <motion.button
              key={earning.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + index * 0.05 }}
              whileHover={{ scale: 1.01, y: -2 }}
              onClick={() => navigate(`/earnings/${earning.id}`)}
              className="w-full p-6 bg-white border border-gray-200 rounded-2xl hover:border-gray-900 transition-all duration-300 group"
            >
              <div className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-6 flex-1">
                  {/* Company Info */}
                  <div className="text-left flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="text-gray-900">{earning.company}</h4>
                      <span className="px-3 py-1 bg-gray-100 rounded-lg text-gray-600">
                        {earning.ticker}
                      </span>
                    </div>
                    <p className="text-gray-500">{earning.sector}</p>
                  </div>

                  {/* Date and Time */}
                  <div className="text-left min-w-[180px]">
                    <p className="text-gray-900 mb-1">{earning.date}</p>
                    <p className="text-gray-500">{earning.time}</p>
                  </div>

                  {/* Expected EPS */}
                  <div className="text-left min-w-[120px]">
                    <p className="text-gray-500 mb-1">Expected EPS</p>
                    <p className="text-gray-900 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      {earning.expectedEPS}
                    </p>
                  </div>

                  {/* Market Cap */}
                  <div className="text-left min-w-[120px]">
                    <p className="text-gray-500 mb-1">Market Cap</p>
                    <p className="text-gray-900">{earning.marketCap}</p>
                  </div>
                </div>

                {/* Arrow */}
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-1 transition-all" />
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Empty State */}
        {filteredEarnings.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <p className="text-gray-500">No earnings found matching your search</p>
          </motion.div>
        )}
      </main>
    </div>
  );
}

